import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  closestCorners,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';
import UserAvatars from './UserAvatars';

// Компонент для сортируемой задачи
const SortableTask = ({ task, onDelete, onCopy, onEdit, currentLang, deletingTasks, updatingTasks, isTaskEditor, getSiteName, isTaskOverdue, getDaysOverdue, onMessagesClick, onFilesClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id,
    data: {
      type: 'task',
      task: task,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  // Функция для очистки HTML
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  const isOverdue = isTaskOverdue ? isTaskOverdue(task) : false;
  const daysOverdue = isOverdue && getDaysOverdue ? getDaysOverdue(task.d_end) : 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`bg-gray-50 rounded-lg p-3 border-l-4 hover:shadow-md transition-all duration-200 relative ${
        isDragging ? 'shadow-xl scale-105 rotate-2 z-1 pointer-events-none' : 'z-0'
      } ${updatingTasks.has(task.id) ? 'opacity-75' : ''} ${
        isOverdue ? 'border-red-500 bg-red-50' : 'border-blue-500'
      }`}

    >
      {/* Иконка для перетаскивания */}
      <div
        {...listeners}
        className="absolute top-2 right-2 w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center cursor-move transition-colors duration-200 hover:scale-110 z-10"
        title={currentLang === 'ru' ? 'Перетащить задачу' : 'Drag task'}
      >
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </div>

      <div className="flex justify-between items-start mb-2 pr-8">
        <div className="flex items-center space-x-2 flex-1">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isOverdue ? 'bg-red-500' : 'bg-gray-400'}`}></div>
          <div className="flex items-center space-x-2 flex-1">
          <Link 
            to={`/seo/tasks/${task.id}`}
              className={`text-sm font-medium hover:text-blue-600 ${isOverdue ? 'text-red-700' : 'text-gray-900'}`}
          >
            {task.name}
          </Link>
            
          </div>
          {updatingTasks.has(task.id) && (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
          )}
        </div>
      </div>
      
      <div className="text-xs text-gray-500 mb-1">
        <span className="font-medium text-gray-600">
          {getSiteName ? getSiteName(task.site_id) : ''}
        </span>
      </div>
      
      {task.text && (
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
          {stripHtml(task.text)}
        </p>
      )}
      
      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
        <div className="flex items-center space-x-1">
          <span>{formatDate(task.d_start)} - </span>
          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>{formatDate(task.d_end)}</span>
        </div>
        <span>{task.pct}%</span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-1 mb-2">
        <div 
          className="bg-blue-600 h-1 rounded-full" 
          style={{ width: `${task.pct}%` }}
        ></div>
      </div>

      {/* Аватары пользователей и иконки действий */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMessagesClick && onMessagesClick(task.id);
            }}
            className="text-blue-500 hover:text-blue-700 p-1"
            title={currentLang === 'ru' ? 'Комментарии' : 'Comments'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onFilesClick && onFilesClick(task.id);
            }}
            className="text-green-500 hover:text-green-700 p-1"
            title={currentLang === 'ru' ? 'Файлы' : 'Files'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy(task);
            }}
            className="text-blue-500 hover:text-blue-700"
            title={currentLang === 'ru' ? 'Копировать задачу' : 'Copy task'}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          {isTaskEditor && isTaskEditor(task) && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit && onEdit(task);
                }}
                className="text-yellow-500 hover:text-yellow-700"
                title={currentLang === 'ru' ? 'Редактировать' : 'Edit'}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task.id);
                }}
                disabled={deletingTasks.has(task.id)}
                className="text-red-500 hover:text-red-700 disabled:opacity-50"
                title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
              >
                {deletingTasks.has(task.id) ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-500"></div>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </>
          )}
          {isOverdue && (
              <span className="px-1.5 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-800 flex-shrink-0">
                {daysOverdue} {currentLang === 'ru' ? 'дн.' : 'days'}
              </span>
          )}
        </div>
        <UserAvatars 
          users={task.access_users || []} 
          maxVisible={2} 
          size="xs" 
        />
      </div>
    </div>
  );
};

// Компонент колонки статуса
const StatusColumn = ({ status, tasks, onDelete, onCopy, onEdit, currentLang, deletingTasks, updatingTasks, getStatusText, getStatusColor, isTaskEditor, getSiteName, isTaskOverdue, getDaysOverdue, onMessagesClick, onFilesClick, isDragging, dragOverColumn }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `status-${status}`,
    data: { status }
  });
  

  



  return (
    <div 
      ref={setNodeRef}
      className={`bg-white rounded-lg shadow-md p-4 h-full flex flex-col transition-all duration-200 relative overflow-hidden ${
        isDragging ? (isOver ? 'bg-blue-50 border-2 border-dashed border-blue-400' : 'bg-gray-50 border-2 border-dashed border-gray-300') : ''
      } ${isDragging ? 'pointer-events-auto' : ''}`}
      data-status={status}
      data-is-over={isOver}

    >


      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-semibold ${getStatusColor(status)} px-3 py-1 rounded-full`}>
          {getStatusText(parseInt(status))} ({tasks.length})
        </h3>
      </div>
      
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="space-y-3 flex-shrink-0">
          {tasks.map((task) => (
            <SortableTask
              key={task.id}
              task={task}
              onDelete={onDelete}
              onCopy={onCopy}
              onEdit={onEdit}
              currentLang={currentLang}
              deletingTasks={deletingTasks}
              updatingTasks={updatingTasks}
              isTaskEditor={isTaskEditor}
              getSiteName={getSiteName}
              isTaskOverdue={isTaskOverdue}
              getDaysOverdue={getDaysOverdue}
              onMessagesClick={onMessagesClick}
              onFilesClick={onFilesClick}
            />
          ))}
        </div>
      </div>
      
      {/* Отдельная drop-зона, которая перекрывает всю колонку во время перетаскивания */}
      {isDragging && (
        <div 
          className="absolute inset-0 bg-transparent z-10"
          data-status={status}
          data-is-over={dragOverColumn === status}
        />
      )}
    </div>
  );
};

// Основной компонент канбана
const KanbanBoard = ({ 
  tasks, 
  onDelete, 
  onCopy,
  onEdit,
  onStatusChange, 
  currentLang, 
  deletingTasks,
  getStatusText,
  getStatusColor,
  isTaskEditor,
  getSiteName,
  isTaskOverdue,
  getDaysOverdue,
  onMessagesClick,
  onFilesClick
}) => {
  const [updatingTasks, setUpdatingTasks] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Группируем задачи по статусам
  const getTasksByStatus = () => {
    const grouped = {};
    tasks.forEach(task => {
      const status = task.status.toString();
      if (!grouped[status]) {
        grouped[status] = [];
      }
      grouped[status].push(task);
    });
    return grouped;
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active) {
      const activeTask = tasks.find(task => task.id === active.id);
      
      let newStatus = null;
      
      // Определяем статус по колонке или задаче
      if (dragOverColumn !== null) {
        newStatus = parseInt(dragOverColumn);
      } else if (over && typeof over.id === 'number') {
        // Перетаскивание на задачу - определяем статус по контексту колонки
        const targetTask = tasks.find(task => task.id === over.id);
        
        if (targetTask && targetTask.id !== activeTask.id) {
          newStatus = targetTask.status;
        }
      }
      
      if (newStatus !== null && activeTask) {
        const currentStatus = activeTask.status;
        
        // Проверяем, что статус действительно изменился
        if (currentStatus !== newStatus) {
          // Показываем индикатор загрузки
          setUpdatingTasks(prev => new Set(prev).add(activeTask.id));
          
          try {
            // Обновляем статус задачи
            await onStatusChange(activeTask.id, newStatus);
          } finally {
            // Скрываем индикатор загрузки
            setUpdatingTasks(prev => {
              const newSet = new Set(prev);
              newSet.delete(activeTask.id);
              return newSet;
            });
          }
        }
      }
    }
  };

  const tasksByStatus = getTasksByStatus();
  const statuses = ['0', '1', '2', '99']; // Ожидает, В работе, Завершена, Отменена

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={(event) => {
        setIsDragging(true);
      }}
      onDragOver={(event) => {
        const { over } = event;
        
        if (over && typeof over.id === 'string' && over.id.startsWith('status-')) {
          const columnStatus = over.id.replace('status-', '');
          setDragOverColumn(columnStatus);
        }
      }}
      onDragEnd={(event) => {
        setIsDragging(false);
        handleDragEnd(event);
        setDragOverColumn(null);
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 h-full">
        {statuses.map((status) => (
          <div key={status} data-status={status} className="h-full">
            <StatusColumn
              status={status}
              tasks={tasksByStatus[status] || []}
              onDelete={onDelete}
              onCopy={onCopy}
              onEdit={onEdit}
              currentLang={currentLang}
              deletingTasks={deletingTasks}
              updatingTasks={updatingTasks}
              getStatusText={getStatusText}
              getStatusColor={getStatusColor}
              isTaskEditor={isTaskEditor}
              getSiteName={getSiteName}
              isTaskOverdue={isTaskOverdue}
              getDaysOverdue={getDaysOverdue}
              onMessagesClick={onMessagesClick}
              onFilesClick={onFilesClick}
              isDragging={isDragging}
              dragOverColumn={dragOverColumn}
            />
          </div>
        ))}
      </div>
    </DndContext>
  );
};

export default KanbanBoard; 